import { useState, useEffect, useCallback, useRef } from 'react';
import googleDriveService from '../services/googleDriveService';
import { AUTO_SAVE_DELAY, FEATURE_FLAGS } from '../utils/constants';
import { showNotification } from '../utils/helpers';

/**
 * Custom hook for Google Drive integration
 * @param {Array} foods - Foods data to sync
 * @param {Array} selectedFoods - Selected foods data to sync
 * @param {Array} baseFoods - Base foods to compare for auto-save trigger
 * @returns {Object} Google Drive state and methods
 */
const useGoogleDrive = (foods = [], selectedFoods = [], baseFoods = []) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [googleUser, setGoogleUser] = useState(null);
  const [error, setError] = useState(null);
  const [isInitializing, setIsInitializing] = useState(true);

  // Refs to track previous values and prevent infinite loops
  const previousFoodsLengthRef = useRef(foods.length);
  const autoSaveTimeoutRef = useRef(null);

  // Initialize Google Drive services on mount
  useEffect(() => {
    if (!FEATURE_FLAGS.GOOGLE_DRIVE_SYNC) {
      setIsInitializing(false);
      return;
    }

    const initializeServices = async () => {
      try {
        setIsInitializing(true);
        setError(null);

        await googleDriveService.initializeGapi();
        
        // Check if already connected
        const status = googleDriveService.getStatus();
        if (status.isConnected) {
          setIsConnected(true);
          setGoogleUser(status.userInfo);
          console.log('✅ Already connected to Google Drive');
        }
        
      } catch (initError) {
        console.error('❌ Failed to initialize Google Drive services:', initError);
        setError(`Échec de l'initialisation: ${initError.message}`);
      } finally {
        setIsInitializing(false);
      }
    };

    initializeServices();
  }, []);

  /**
   * Connect to Google Drive
   */
  const connect = useCallback(async () => {
    setError(null);
    setIsSyncing(true);
    
    try {
      console.log('🔄 Connecting to Google Drive...');
      const result = await googleDriveService.connect();
      
      setIsConnected(true);
      setGoogleUser(result.userInfo);
      
      showNotification(
        `Connecté à Google Drive en tant que ${result.userInfo.getName()}`, 
        'success'
      );
      
      console.log('✅ Successfully connected to Google Drive');
      
    } catch (connectError) {
      console.error('❌ Connection error:', connectError);
      setError(connectError.message);
      showNotification(`Erreur de connexion: ${connectError.message}`, 'error');
    } finally {
      setIsSyncing(false);
    }
  }, []);

  /**
   * Disconnect from Google Drive
   */
  const disconnect = useCallback(async () => {
    try {
      await googleDriveService.disconnect();
      setIsConnected(false);
      setGoogleUser(null);
      setLastSyncTime(null);
      setError(null);
      showNotification('Déconnecté de Google Drive', 'info');
      console.log('✅ Disconnected from Google Drive');
    } catch (disconnectError) {
      console.error('❌ Disconnect error:', disconnectError);
      setError('Erreur lors de la déconnexion');
      showNotification('Erreur lors de la déconnexion', 'error');
    }
  }, []);

  /**
   * Save data to Google Drive
   */
  const saveToGoogleDrive = useCallback(async () => {
    if (!isConnected) {
      console.log('⚠️ Not connected to Google Drive, skipping save');
      return;
    }
    
    setIsSyncing(true);
    setError(null);
    
    try {
      console.log('💾 Saving data to Google Drive...');
      
      const dataToSave = {
        foods: foods,
        selectedFoods: selectedFoods,
        lastUpdated: new Date().toISOString(),
        version: '2.0',
        appVersion: process.env.REACT_APP_VERSION || '2.0.0',
        userAgent: navigator.userAgent,
        exportedAt: new Date().toISOString()
      };

      await googleDriveService.uploadData(dataToSave);
      setLastSyncTime(new Date());
      
      console.log('✅ Data saved to Google Drive successfully');
      showNotification('Données sauvegardées sur Google Drive', 'success');
      
    } catch (saveError) {
      console.error('❌ Error saving to Google Drive:', saveError);
      setError(`Erreur de sauvegarde: ${saveError.message}`);
      showNotification(`Erreur de sauvegarde: ${saveError.message}`, 'error');
    } finally {
      setIsSyncing(false);
    }
  }, [isConnected, foods, selectedFoods]);

  /**
   * Load data from Google Drive
   */
  const loadFromGoogleDrive = useCallback(async () => {
    if (!isConnected) {
      console.log('⚠️ Not connected to Google Drive, skipping load');
      return null;
    }
    
    setIsSyncing(true);
    setError(null);
    
    try {
      console.log('📥 Loading data from Google Drive...');
      
      const savedData = await googleDriveService.downloadData();
      
      if (savedData && savedData.foods) {
        setLastSyncTime(new Date());
        console.log('✅ Data loaded from Google Drive successfully');
        showNotification('Données restaurées depuis Google Drive', 'success');
        
        // Validate data structure
        if (Array.isArray(savedData.foods)) {
          return savedData;
        } else {
          throw new Error('Format de données invalide');
        }
      } else {
        console.log('ℹ️ No valid data found in backup file');
        showNotification('Aucune donnée trouvée dans la sauvegarde', 'info');
        return null;
      }
    } catch (loadError) {
      console.error('❌ Error loading from Google Drive:', loadError);
      setError(`Erreur de chargement: ${loadError.message}`);
      showNotification(`Erreur de chargement: ${loadError.message}`, 'error');
      return null;
    } finally {
      setIsSyncing(false);
    }
  }, [isConnected]);

  /**
   * Manual sync (save then optionally load)
   */
  const manualSync = useCallback(async (includeLoad = false) => {
    console.log('🔄 Starting manual sync...');
    
    try {
      // Always save current data first
      await saveToGoogleDrive();
      
      // Optionally load data (for restore functionality)
      if (includeLoad) {
        return await loadFromGoogleDrive();
      }
      
      return null;
    } catch (syncError) {
      console.error('❌ Manual sync failed:', syncError);
      throw syncError;
    }
  }, [saveToGoogleDrive, loadFromGoogleDrive]);

  /**
   * Restore data from Google Drive
   */
  const restoreData = useCallback(async () => {
    const confirmed = window.confirm(
      'Êtes-vous sûr de vouloir restaurer les données depuis Google Drive ? ' +
      'Cela remplacera vos données actuelles.'
    );
    
    if (!confirmed) return null;
    
    return await loadFromGoogleDrive();
  }, [loadFromGoogleDrive]);

  /**
   * Test Google Drive connection
   */
  const testConnection = useCallback(async () => {
    if (!isConnected) return false;
    
    try {
      const isWorking = await googleDriveService.testConnection();
      if (!isWorking) {
        setError('Connexion Google Drive défaillante');
      }
      return isWorking;
    } catch (testError) {
      console.error('❌ Connection test failed:', testError);
      setError('Test de connexion échoué');
      return false;
    }
  }, [isConnected]);

  // Auto-save when data changes (only if connected and data has actually changed)
  useEffect(() => {
    // Only trigger if:
    // 1. Connected to Google Drive
    // 2. Not currently syncing
    // 3. Foods length has actually changed (not just re-render)
    // 4. Foods array has more items than base foods
    const currentFoodsLength = foods.length;
    const hasChanged = currentFoodsLength !== previousFoodsLengthRef.current;

    if (isConnected && !isSyncing && hasChanged && currentFoodsLength > baseFoods.length) {
      console.log(`⏰ Auto-save triggered: ${previousFoodsLengthRef.current} → ${currentFoodsLength} foods`);

      // Clear any existing timeout
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }

      // Set new timeout
      autoSaveTimeoutRef.current = setTimeout(() => {
        saveToGoogleDrive();
        autoSaveTimeoutRef.current = null;
      }, AUTO_SAVE_DELAY);

      // Update the ref to current length
      previousFoodsLengthRef.current = currentFoodsLength;

      return () => {
        if (autoSaveTimeoutRef.current) {
          clearTimeout(autoSaveTimeoutRef.current);
        }
      };
    }
  }, [foods.length, selectedFoods, isConnected, isSyncing, saveToGoogleDrive, baseFoods.length]);

  // Periodic connection test (every 5 minutes when connected)
  useEffect(() => {
    if (!isConnected) return;
    
    const intervalId = setInterval(() => {
      testConnection();
    }, 5 * 60 * 1000); // 5 minutes
    
    return () => clearInterval(intervalId);
  }, [isConnected, testConnection]);

  return {
    // State
    isConnected,
    isSyncing,
    lastSyncTime,
    googleUser,
    error,
    isInitializing,
    
    // Methods
    connect,
    disconnect,
    saveToGoogleDrive,
    loadFromGoogleDrive,
    manualSync,
    restoreData,
    testConnection,
    
    // Computed
    canSync: isConnected && !isSyncing && !isInitializing,
    isReady: !isInitializing,
    status: {
      connected: isConnected,
      syncing: isSyncing,
      error: error,
      lastSync: lastSyncTime,
      user: googleUser?.getName() || null
    }
  };
};

export { useGoogleDrive };
export default useGoogleDrive;